import React, { useEffect, useRef } from "react";
import { FaInfoCircle } from "react-icons/fa";
import {
  PORTAL_LABELS,
  PORTAL_OWN_DOMAIN,
  PORTAL_SHARED,
  emailDomain,
  isBlockedPersonalEmail,
  isValidPortalDomain,
  normalizePortalDomain,
} from "../utils/loginDomain";
import "../assets/css/PortalLoginFields.css";

export const SHARED_PORTAL_HOST = "crm.digixleads.com";
function InfoIcon({ text }) {
  return (
    <FaInfoCircle
      className="services-info-icon portal-login-fields__info"
      title={text}
      aria-label={text}
    />
  );
}

export function portalHostFor(portalType, portalDomain) {
  const domain = normalizePortalDomain(portalDomain);
  return portalType === PORTAL_OWN_DOMAIN && domain ? `office.${domain}` : SHARED_PORTAL_HOST;
}

/** Client-side check before submit; the API repeats it. Returns an error message or "". */
export function portalSettingsError(portalType, portalDomain) {
  if (portalType !== PORTAL_OWN_DOMAIN) return "";
  const domain = normalizePortalDomain(portalDomain);
  if (!domain) return "Enter the customer's domain (example: acme.com) or choose Digix Leads CRM.";
  if (!isValidPortalDomain(domain)) return `'${portalDomain}' is not a valid domain (example: acme.com).`;
  if (["tumma.co", "digixleads.com"].some((d) => domain === d || domain.endsWith(`.${d}`))) {
    return `'${domain}' is reserved and cannot be a client domain.`;
  }
  return "";
}

/** Customer Domain: the owner login must be on that domain, even when it is an existing login. */
export function ownerDomainError(ownerEmail, portalType, portalDomain) {
  if (portalType !== PORTAL_OWN_DOMAIN) return "";
  const domain = normalizePortalDomain(portalDomain);
  if (!domain) return "";
  return emailDomain(ownerEmail) === domain
    ? ""
    : `Owner Login Email must end with @${domain} for a Customer Domain client.`;
}

/** Customer domain taken from the owner's login email (business domains only). */
export function domainFromOwnerEmail(ownerEmail) {
  const domain = emailDomain(ownerEmail);
  if (!domain || isBlockedPersonalEmail(ownerEmail) || !isValidPortalDomain(domain)) return "";
  if (portalSettingsError(PORTAL_OWN_DOMAIN, domain)) return "";
  return domain;
}

/**
 * Customer Domain owner email for the (new) domain: keeps a business local part
 * ("ashok@old.com" -> "ashok@new.com"), otherwise admin@<domain>. Returns the email unchanged
 * when it already matches or the domain is not usable yet.
 */
export function autoOwnerEmail(ownerEmail, portalType, portalDomain) {
  const current = String(ownerEmail || "").trim();
  if (portalType !== PORTAL_OWN_DOMAIN) return current;
  const domain = normalizePortalDomain(portalDomain);
  if (!domain || portalSettingsError(portalType, domain)) return current;
  if (emailDomain(current) === domain) return current;
  const local = current.includes("@") && !isBlockedPersonalEmail(current) ? current.split("@")[0] : "";
  return `${local || "admin"}@${domain}`;
}

/** Hint under the owner login email for the chosen portal type. */
export function ownerEmailHint(portalType, portalDomain) {
  const domain = normalizePortalDomain(portalDomain);
  if (portalType === PORTAL_OWN_DOMAIN) {
    return domain
      ? `Must end with @${domain} (Customer Domain), even for an existing login. Signs in at office.${domain}.`
      : "Customer Domain: filled in as admin@<domain> once the customer domain is entered.";
  }
  return `Any email (Gmail etc. allowed). Signs in at ${SHARED_PORTAL_HOST}. The password you set is saved to this client's account logins.`;
}

export function ownerEmailPlaceholder(portalType, portalDomain) {
  const domain = normalizePortalDomain(portalDomain);
  return portalType === PORTAL_OWN_DOMAIN && domain ? `admin@${domain}` : "owner@gmail.com";
}

/**
 * "Portal & login" section: where this tenant's people sign in.
 * - Digix Leads CRM (Shared): crm.digixleads.com with any email.
 * - Customer Domain (Own Domain): office.<domain> with @<domain> emails (DNS + nginx set up by us).
 */
function PortalLoginFields({
  portalType,
  portalDomain,
  suggestedDomain,
  ownerEmail,
  onChange,
  classPrefix = "addtenant",
  syncWithSuggested = false,
}) {
  const isOwn = portalType === PORTAL_OWN_DOMAIN;
  const domain = normalizePortalDomain(portalDomain);
  const error = isOwn && portalDomain ? portalSettingsError(portalType, portalDomain) : "";
  const suggestedFromCompany = normalizePortalDomain(suggestedDomain);
  const suggestion =
    (isValidPortalDomain(suggestedFromCompany) && !portalSettingsError(PORTAL_OWN_DOMAIN, suggestedFromCompany)
      ? suggestedFromCompany
      : "") || domainFromOwnerEmail(ownerEmail);

  // Keep the portal domain following the company's Domain Name until someone types a different one.
  const lastSuggestionRef = useRef(suggestion);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const previous = lastSuggestionRef.current;
    lastSuggestionRef.current = suggestion;
    if (!syncWithSuggested || !isOwn || !suggestion || suggestion === previous) return;
    if (!domain || domain === previous) {
      onChangeRef.current({ portalType, portalDomain: suggestion });
    }
  }, [suggestion, syncWithSuggested, isOwn, domain, portalType]);

  const setType = (type) => {
    const nextDomain =
      type === PORTAL_OWN_DOMAIN && !portalDomain && isValidPortalDomain(suggestion) ? suggestion : portalDomain;
    onChange({ portalType: type, portalDomain: nextDomain });
  };

  const sectionInfo = "Where this client's owner and employees sign in.";
  const sharedInfo = `No domain needed. Signs in at ${SHARED_PORTAL_HOST} with their own email (Gmail etc.); staff set the password.`;
  const customerInfo = "Signs in at office.<domain> with company emails of that domain.";
  const urlInfo = isOwn
    ? `Enter the customer's domain. All logins must end with @${domain || "domain"}. Before they can sign in, ` +
      `the customer points office.${domain || "<domain>"} (DNS A/CNAME) to our server and we add it to nginx with SSL.`
    : "Shared portal for clients without their own domain.";

  return (
    <div className={`form-section ${classPrefix}-section-divider portal-login-fields`}>
      <h3 className={`section-title ${classPrefix}-subsection-title`}>
        Portal &amp; Login
        <InfoIcon text={sectionInfo} />
      </h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Portal Type</label>
          <div className="portal-login-fields__options" role="radiogroup" aria-label="Portal type">
            <label className="portal-login-fields__option">
              <input
                type="radio"
                name="portalType"
                value={PORTAL_SHARED}
                checked={!isOwn}
                onChange={() => setType(PORTAL_SHARED)}
              />
              <span>{PORTAL_LABELS[PORTAL_SHARED]}</span>
              <InfoIcon text={sharedInfo} />
            </label>
            <label className="portal-login-fields__option">
              <input
                type="radio"
                name="portalType"
                value={PORTAL_OWN_DOMAIN}
                checked={isOwn}
                onChange={() => setType(PORTAL_OWN_DOMAIN)}
              />
              <span>{PORTAL_LABELS[PORTAL_OWN_DOMAIN]}</span>
              <InfoIcon text={customerInfo} />
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>
            Portal URL {isOwn ? <span className="required">*</span> : null}
            <InfoIcon text={urlInfo} />
          </label>
          {isOwn ? (
            <div className={`portal-login-fields__url${error ? " has-error" : ""}`}>
              <span className="portal-login-fields__url-prefix">https://office.</span>
              <input
                type="text"
                name="portalDomain"
                value={portalDomain || ""}
                onChange={(e) => onChange({ portalType, portalDomain: e.target.value })}
                onBlur={() => {
                  if (portalDomain && domain !== portalDomain) onChange({ portalType, portalDomain: domain });
                }}
                placeholder="acme.com"
                autoComplete="off"
                aria-label="Customer domain"
              />
            </div>
          ) : (
            <input type="text" value={`https://${SHARED_PORTAL_HOST}`} readOnly className="portal-login-fields__readonly" />
          )}
          {error ? <small className="form-hint portal-login-fields__error">{error}</small> : null}
        </div>
      </div>
    </div>
  );
}

export default PortalLoginFields;
